export interface ReportOptionItem {
  code: string;
  name: string;
}

export interface ReportOptionLists {
  companySizes: ReportOptionItem[];
  companyLimitedBy: ReportOptionItem[];
  taxonomies: ReportOptionItem[];
  companyTypes: ReportOptionItem[];
  incomeStatementNames: ReportOptionItem[];
  financialPositionNames: ReportOptionItem[];
  currencies: ReportOptionItem[];
  shareTypes: ReportOptionItem[];
  shareClasses: ReportOptionItem[];
}

/** statementArea: 0 Profit and loss, 1 Balance sheet. */
export interface ReportHeadingRow {
  key: string;
  statementArea: number;
  defaultName: string;
  amendedName: string | null;
  displayName: string;
  indentLevel: number;
  accountGroupName: string | null;
}

export interface SaveReportHeadingsPayload {
  clientId: string;
  headings: { key: string; amendedName: string | null }[];
}

export interface ReportSettings {
  annualReportName: string | null;
  managementReportName: string | null;
  abbreviatedReportName: string | null;
  filedReportName: string | null;
  companySize: string;
  companyLimitedBy: string;
  taxonomy: string;
  companyType: string;
  incomeStatementName: string;
  financialPositionName: string;
  currencyCode: string;
  tangibleAssetsNote: boolean;
  intangibleAssetsNote: boolean;
  debtorsNote: boolean;
  creditorsNote: boolean;
  detailedProfitAndLoss: boolean;
  showOperatingProfit: boolean;
  showInterestPayable: boolean;
  showTaxOnProfit: boolean;
  showCreditorsAfterOneYear: boolean;
  showCreditorsWithinOneYear: boolean;
  showRetainedEarnings: boolean;
  priorYearRestated: boolean;
  showResignedDirectors: boolean;
  showDateApproved: boolean;
  /** BUG-072: an LLP's "Member's Interest" option. */
  membersInterest?: boolean;
  /** 0 Trading, 1 Dormant. */
  tradingStatus: number;
  /**
   * Legacy mixed radio (0 Audited, 1 Disclose debtors more than one year in the balance sheet). ap5-a5 split it
   * into auditStatus and discloseDebtorsAfterOneYear; it is still sent so older rows stay consistent.
   */
  reportType: number;
  /** ap5-a5: show debtors due after more than one year as their own balance sheet line. */
  discloseDebtorsAfterOneYear: boolean;
  /**
   * cap3-f: null until the user chooses on the Report Type radio; 1 audit exempt, 2 audited. Sent back unchanged by
   * autosave, so saving other fields never marks the accounts audited.
   */
  auditStatus?: number | null;
}

export interface ReportSettingsView extends ReportSettings {
  clientId: string;
}

export interface NotesToAccountsView {
  clientId: string;
  generalInformation: string;
}

export interface DirectorOption {
  id: string;
  name: string;
}

/**
 * revisionType: 0 Revision by replacement, 1 Revision by supplementary note, null until the
 * client has chosen. task-112: it used to arrive as 0 for a client who had never chosen, which
 * is indistinguishable from choosing replacement, so the radio pre-selected one for them.
 * saveRevisionType still takes 0 or 1 only.
 */
export interface RevisedAccountsView {
  clientId: string;
  accountingPeriodId: string;
  revisionType: number | null;
  replacementReason: string | null;
  replacementAmendments: string | null;
  supplementaryText: string | null;
  supplementaryDetail: string | null;
  supplementaryApprovalDate: string | null;
  approvingContactId: string | null;
  approvingDirectorName: string | null;
  directors: DirectorOption[];
}

export interface SaveReplacementPayload {
  clientId: string;
  accountingPeriodId: string;
  replacementReason: string | null;
  replacementAmendments: string | null;
}

export interface SaveSupplementaryPayload {
  clientId: string;
  accountingPeriodId: string;
  supplementaryText: string | null;
  supplementaryDetail: string | null;
  supplementaryApprovalDate: string | null;
  approvingContactId: string | null;
}

/** Revised accounts dialogs receive the loaded view and return the saved one. */
export interface RevisedAccountsDialogData {
  view: RevisedAccountsView;
}

export interface ShareCapitalRow {
  id: string;
  shareType: string | null;
  shareTypeName: string;
  issueDate: string;
  noOfShares: number;
  buyBackDate: string | null;
  buyBackShares: number | null;
  shareValue: number | null;
  shareClass: string | null;
  shareClassType: string | null;
  shareClassLabel: string;
  authorisedShares: number | null;
  allottedShares: number | null;
  isPartlyPaid: boolean;
  /** BUG-030: the name of the class chosen from the list (it may no longer be offered). */
  shareClassTypeName?: string | null;
  /** APT338 follow-up 2: the amount paid on each share of a partly paid class; the unpaid part is the share value less it. Null when not recorded. */
  paidUpPerShare?: number | null;
}

export interface ShareCapitalView {
  clientId: string;
  displayAuthorisedShares: boolean;
  shares: ShareCapitalRow[];
}

export interface SaveShareCapitalPayload {
  clientId: string;
  id: string | null;
  shareType: string | null;
  issueDate: string | null;
  noOfShares: number | null;
  buyBackDate: string | null;
  buyBackShares: number | null;
  shareValue: number | null;
  shareClass: string | null;
  shareClassType: string | null;
  authorisedShares: number | null;
  allottedShares: number | null;
  isPartlyPaid: boolean;
  /** APT338 follow-up 2: sent only for a partly paid class. */
  paidUpPerShare?: number | null;
}

export interface ShareCapitalDialogData {
  clientId: string;
  share?: ShareCapitalRow;
}

const ukDate = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;
const isoDate = /^(\d{4})-(\d{2})-(\d{2})/;

/** Reads a typed date (dd/mm/yyyy, or yyyy-mm-dd) into yyyy-MM-dd; returns null when empty or not a real date. */
export function parseTypedDate(value: any): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value?.format === 'function') {
    return value.format('YYYY-MM-DD');
  }
  const text = `${value}`.trim();
  let year: number;
  let month: number;
  let day: number;
  let match = ukDate.exec(text);
  if (match) {
    day = +match[1];
    month = +match[2];
    year = +match[3];
  } else {
    match = isoDate.exec(text);
    if (!match) {
      return null;
    }
    year = +match[1];
    month = +match[2];
    day = +match[3];
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return `${year}-${`${month}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;
}

/** yyyy-MM-dd from the API shown as dd/mm/yyyy. */
export function formatDisplayDate(value: string | null | undefined): string {
  const match = value ? isoDate.exec(value) : null;
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/** Validator-friendly check for typed dates; empty passes (use Validators.required for mandatory dates). */
export function typedDateValidator(control: { value: any }): { [key: string]: boolean } | null {
  const value = control.value;
  if (value === null || value === undefined || `${value}`.trim() === '') {
    return null;
  }
  return parseTypedDate(value) ? null : { date: true };
}

/** Empty text becomes null; anything else becomes a number (NaN is rejected by the form validators first). */
export function toNumberOrNull(value: any): number | null {
  if (value === null || value === undefined || `${value}`.trim() === '') {
    return null;
  }
  const parsed = Number(`${value}`.replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
}
